import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementProperties,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDataProperties,
  type CanvasElementInputBinding,
  type CanvasElementPropertiesWithData,
  type DataBindingSourceInfo,
  type DataBindingSourceInfoColumn,
  type ElementPropUpdate,
  findWorkflowGetSourceNode,
  getActiveBinding,
  isBindingActive,
} from '@blaze-shared/elements-domain';
import { type BaseSchema, type DatasetColumn } from '@blaze-shared/schema-domain';
import {
  type ApiCallWorkflowNode,
  findFirstNodeInWorkflowOfType,
  type GetWorkflowNode,
  isAggregateNode,
  isApiCallNode,
  isComputeNode,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { hasDataProps } from './element-props-guards';
import { getDataSourceInputName } from './io';

export const createElementDataPatch = (
  elementId: CanvasElementId,
  bindingKey: CanvasElementInputBinding,
  workflowId: string,
  columns: DatasetColumn[] = [],
  isCustomSelection = false,
  bindings: Record<string, string> = {},
  dataSourceName?: string,
): ElementPropUpdate<CanvasElementDataProperties> => ({
  elementId,
  path: `data.?key:${bindingKey}`,
  value: createElementDataSourceBinding(
    bindingKey,
    workflowId,
    dataSourceName,
    columns,
    isCustomSelection,
    bindings,
  ),
});

export const createElementDataSourceBinding = (
  bindingKey: CanvasElementInputBinding,
  workflowId: string,
  dataSourceName?: string,
  columns: DatasetColumn[] = [],
  isCustomSelection = false,
  bindings: Record<string, string> = {},
): CanvasElementDataProperties => ({
  key: bindingKey,
  dataSource: {
    sourceType: 'context',
    id: workflowId,
    columns,
    name: dataSourceName,
  },
  bindings,
  enabled: true,
  isCustomSelection,
});

export const getColumnsFromSchema = (schema: BaseSchema): DataBindingSourceInfoColumn[] => {
  if (!schema.columns?.length) {
    console.error(`No columns in dataset "${schema.id}": "${schema.name}"`);
    return [];
  }
  return schema.columns.map((col) => ({
    ...col,
    isId: col.isId || false,
    isComputed: col.isComputed || false,
  }));
};

export const filterOutInternalColumns = (
  cols: DataBindingSourceInfoColumn[],
): DataBindingSourceInfoColumn[] => cols.filter((c) => c.isId || !c.isInternal);

export const filterOutSecureColumns = (
  cols: DataBindingSourceInfoColumn[],
): DataBindingSourceInfoColumn[] => cols.filter((c) => c.format?.formatType !== 'SecureText');

export const getFormlikeBinding = (
  elementProps: CanvasElementProperties,
): CanvasElementDataProperties | undefined =>
  elementProps
    ? (elementProps as CanvasElementPropertiesWithData).data?.find(
        (b) =>
          isBindingActive(b) &&
          b.dataSource &&
          (b.dataSource.sourceType === 'formContainer' ||
            b.dataSource.sourceType === 'editableList'),
      )
    : undefined;

export function getActiveInputDataSource(
  element: CanvasElement,
): DataBindingSourceInfo | undefined {
  if (!hasDataProps(element)) {
    return;
  }

  const bindingKey: CanvasElementInputBinding = getDataSourceInputName(element.type);
  const binding = getActiveBinding(element.props.data, bindingKey);

  return binding?.dataSource;
}

/** Returns the displayed source node for the workflow. `undefined` if workflow could not be detected as a source */
export function findWorkflowSourceNode(
  workflow: Workflow,
): GetWorkflowNode | ApiCallWorkflowNode | undefined {
  const getNode = findWorkflowGetSourceNode(workflow);
  const apiCallNode = findFirstNodeInWorkflowOfType(workflow, isApiCallNode);

  const nodes = workflow.nodes;

  if (
    getNode?.datasetId &&
    (nodes.length === 1 ||
      (nodes.length === 2 && isAggregateNode(nodes[1]) && nodes[1].id.startsWith('chart-')) ||
      (nodes.length === 2 && isComputeNode(nodes[1]) && nodes[1].id.startsWith('chart-compute')) ||
      (nodes.length < 6 &&
        nodes
          .filter((_, i) => i > 0 && i < nodes.length - 2)
          .every((node) => isComputeNode(node) && node.id.startsWith('chart-compute')) &&
        isAggregateNode(nodes[nodes.length - 1])))
  ) {
    return getNode;
  } else if (apiCallNode && nodes.length === 1) {
    return apiCallNode;
  } else {
    return undefined;
  }
}
